import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useChatContextStore } from '@/stores/useChatContextStore'
import { useCrmStore, selectSelectedLead, selectSelectedContact } from '@/stores/useCrmStore'
import type { RouteContext } from '@/stores/useChatContextStore'
import { ADVANCED_PAGE, SETTINGS_PAGES, SETTINGS_PATHS } from '@/pages/settings/settingsPages'

// Map routes to human-readable page names
const routeToPageName: Record<string, { name: string; section?: string }> = {
  // #2567: the 8 consolidated screens.
  '/dashboard': { name: 'Dashboard' },
  '/chat': { name: 'Marketing' },
  '/calendar': { name: 'Calendar', section: 'Content' },
  '/leads': { name: 'Leads List', section: 'CRM' },
  '/inbox': { name: 'Inbox' },
  '/results': { name: 'Results' },
  '/files': { name: 'Files' },
  '/library': { name: 'Library' },
  '/media': { name: 'Media Library' },
  '/campaigns': { name: 'Campaigns' },
  '/crm/contacts': { name: 'Customers', section: 'CRM' },
  '/crm/bookings': { name: 'Bookings', section: 'CRM' },
  '/content': { name: 'Content', section: 'Content' },
  '/gmb/posts': { name: 'Posts', section: 'Google Business Profile' },
  '/gmb/insights': { name: 'Insights', section: 'Google Business Profile' },
  '/ads/campaigns': { name: 'Campaigns', section: 'Ads Manager' },
  '/ads/performance': { name: 'Performance Dashboard', section: 'Ads Manager' },
  '/ads/settings': { name: 'Ad Settings', section: 'Ads Manager' },
  '/attribution/dashboard': { name: 'Attribution Dashboard', section: 'Attribution' },
  '/attribution/journeys': { name: 'Customer Journeys', section: 'Attribution' },
  '/settings': { name: 'Settings' },
  ...Object.fromEntries(
    [...SETTINGS_PAGES, ADVANCED_PAGE].map((page) => [
      SETTINGS_PATHS[page.key],
      { name: page.label, section: 'Settings' },
    ])
  ),
}

// Get page info from path, including dynamic routes
function getPageInfo(path: string): { name: string; section?: string } | null {
  // Check exact match first
  if (routeToPageName[path]) {
    return routeToPageName[path]
  }

  // Check dynamic routes
  if (path.startsWith('/leads/') || path.startsWith('/crm/leads/')) {
    return { name: 'Lead Details', section: 'CRM' }
  }
  if (path.startsWith('/crm/contacts/')) {
    return { name: 'Contact Details', section: 'CRM' }
  }
  if (path.startsWith('/content/post/')) {
    return { name: 'Content Post', section: 'Content' }
  }

  return null
}

/**
 * Hook to synchronize chat context with the current route and selected items.
 * This should be used in the AppShell or a similar top-level component.
 */
export function useChatContext() {
  const location = useLocation()
  const setRouteContext = useChatContextStore((state) => state.setRouteContext)
  const setSelectedItem = useChatContextStore((state) => state.setSelectedItem)

  // Get selected items from stores
  const selectedLead = useCrmStore(selectSelectedLead)
  const selectedContact = useCrmStore(selectSelectedContact)

  // Update route context when location changes
  useEffect(() => {
    const pageInfo = getPageInfo(location.pathname)

    if (pageInfo) {
      const routeContext: RouteContext = {
        path: location.pathname,
        pageName: pageInfo.name,
        section: pageInfo.section,
      }
      setRouteContext(routeContext)
    } else {
      setRouteContext(null)
    }
  }, [location.pathname, setRouteContext])

  // Update selected item context when lead selection changes
  useEffect(() => {
    if (selectedLead) {
      setSelectedItem({
        type: 'lead',
        id: selectedLead.id,
        name: selectedLead.name,
        data: selectedLead,
      })
    } else if (selectedContact) {
      setSelectedItem({
        type: 'contact',
        id: selectedContact.id,
        name: selectedContact.name,
        data: selectedContact,
      })
    } else {
      // Only clear if we were on a detail page and are leaving
      // Don't clear just because selectedLead is null on list pages
      const isDetailPage =
        location.pathname.startsWith('/crm/leads/') ||
        location.pathname.startsWith('/crm/contacts/')
      if (!isDetailPage) {
        setSelectedItem(null)
      }
    }
  }, [selectedLead, selectedContact, location.pathname, setSelectedItem])

  return {
    route: useChatContextStore((state) => state.route),
    selectedItem: useChatContextStore((state) => state.selectedItem),
    isContextEnabled: useChatContextStore((state) => state.isContextEnabled),
    clearContext: useChatContextStore((state) => state.clearContext),
    setContextEnabled: useChatContextStore((state) => state.setContextEnabled),
  }
}
